import { useCallback, useState } from 'react';
import { resolverComPrioridade } from '../../domain/usecases/resolverComPrioridade';
import type { PlaylistItem } from '@/shared/models/playlist.model';

const VIBE_TEAM_KEY = 'ds:vibe_team';

/**
 * useVibeQuiz — Batalha de Vibes: pergunta "romance ou sofrência" antes do
 * primeiro pedido quando a batalha está ativa. `inserirPedido` é injetado
 * pelo composer (vem de usePedidoFlow) — o gate de "perguntar antes de
 * inserir" fica no composer (`handlePedir`), que já orquestra fila/cooldown/
 * vibe antes de decidir se insere.
 */
export interface VibeQuizState {
  batalhaVibesAtiva: boolean;
  setBatalhaVibesDb(v: boolean): void;
  pendingVibeItem: PlaylistItem | null;
  setPendingVibeItem(item: PlaylistItem | null): void;
  handlePedirComVibe(vibe: 'romance' | 'sofrencia'): Promise<void>;
  cancelarVibeSelection(): void;
  meuTime: 'romance' | 'sofrencia' | null;
}

export function useVibeQuiz(
  batalhaVibesBroadcast: boolean | null,
  inserirPedido: (item: PlaylistItem, vibe: string) => Promise<void>,
): VibeQuizState {
  const [batalhaVibesDb, setBatalhaVibesDb] = useState(false);
  const [pendingVibeItem, setPendingVibeItem] = useState<PlaylistItem | null>(null);
  const [meuTime, setMeuTime] = useState<'romance' | 'sofrencia' | null>(() => {
    try { return sessionStorage.getItem(VIBE_TEAM_KEY) as 'romance' | 'sofrencia' | null; }
    catch { return null; }
  });

  const handlePedirComVibe = useCallback(async (vibe: 'romance' | 'sofrencia') => {
    if (!pendingVibeItem) return;
    const item = pendingVibeItem;
    setPendingVibeItem(null);
    // Persiste a escolha de time para colorir o tema do app
    try { sessionStorage.setItem(VIBE_TEAM_KEY, vibe); } catch {}
    setMeuTime(vibe);
    await inserirPedido(item, vibe);
  }, [pendingVibeItem, inserirPedido]);

  const cancelarVibeSelection = useCallback(() => {
    setPendingVibeItem(null);
  }, []);

  return {
    batalhaVibesAtiva: resolverComPrioridade(batalhaVibesBroadcast, batalhaVibesDb),
    setBatalhaVibesDb,
    pendingVibeItem,
    setPendingVibeItem,
    handlePedirComVibe,
    cancelarVibeSelection,
    meuTime,
  };
}
